import React, { useState, useCallback } from "react";
import useApiEp from "../../hooks/useApiEp";
import {
  Box,
  Grid,
  Typography,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  InputAdornment,
  Stack,
  Button,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RTooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import SearchIcon from "@mui/icons-material/Search";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PhoneIcon from "@mui/icons-material/Phone";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import FilterListIcon from "@mui/icons-material/FilterList";

// ─── Light theme tokens ─────────────────────────────────────────
const T = {
  bg: "#f5f6fa",
  surface: "#ffffff",
  border: "#e5e7eb",
  borderLight: "#f0f1f5",
  green: "#16a34a",
  greenBg: "#f0fdf4",
  greenBorder: "#bbf7d0",
  red: "#dc2626",
  redBg: "#fef2f2",
  redBorder: "#fecaca",
  blue: "#2563eb",
  blueBg: "#eff6ff",
  blueBorder: "#bfdbfe",
  amber: "#d97706",
  amberBg: "#fffbeb",
  amberBorder: "#fde68a",
  purple: "#7c3aed",
  purpleBg: "#f5f3ff",
  purpleBorder: "#ddd6fe",
  text: "#111827",
  textSecondary: "#374151",
  muted: "#6b7280",
  subtle: "#9ca3af",
  headerBg: "#f9fafb",
};

const card = {
  background: T.surface,
  border: `1px solid ${T.border}`,
  borderRadius: 2,
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

const tooltipStyle = {
  contentStyle: {
    background: "#fff",
    border: `1px solid ${T.border}`,
    color: T.text,
    borderRadius: 6,
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    fontSize: 12,
  },
  labelStyle: { color: T.muted, fontWeight: 600 },
};

// ─── Helpers ────────────────────────────────────────────────────
const fmtDuration = (s) => {
  const n = parseInt(s, 10);
  if (!n || isNaN(n)) return "—";
  return n < 60 ? `${n}s` : `${Math.floor(n / 60)}m ${n % 60}s`;
};

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

// ─── Stat Card ──────────────────────────────────────────────────
const StatCard = ({ label, value, icon, color, bg, border, loading }) => (
  <Card sx={{ ...card, border: `1px solid ${border}` }}>
    <CardContent sx={{ p: "16px 18px", "&:last-child": { pb: "16px" } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              color: T.muted,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.8,
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontSize: 28,
              fontWeight: 800,
              color: T.text,
              lineHeight: 1.2,
              mt: 0.5,
            }}
          >
            {loading ? "—" : value ?? "—"}
          </Typography>
        </Box>
        <Box
          sx={{
            background: bg,
            borderRadius: 1.5,
            p: "8px",
            color,
            display: "flex",
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ─── Main Component ─────────────────────────────────────────────
const VoiceFullReports = () => {
  const [filters, setFilters] = useState({
    answer_status: "",
    campaign_name: "",
    dtmf: "",
    startDate: "",
    endDate: "",
    search: "",
  });
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const buildQS = useCallback(
    (extra = {}) => {
      const p = new URLSearchParams();
      Object.entries({ ...filters, ...extra }).forEach(
        ([k, v]) => v && p.set(k, v)
      );
      return p.toString();
    },
    [filters]
  );

  const { data: stats = {}, isLoading: sLoad } = useApiEp(
    `/voice/get-voice-report-stats?${buildQS()}`
  );
  const { data: listRes = {}, isLoading: lLoad } = useApiEp(
    `/voice/get-voice-report-hooks?${buildQS({ page, limit: 15 })}`
  );

  const setF = (k, v) => {
    setFilters((p) => ({ ...p, [k]: v }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      answer_status: "",
      campaign_name: "",
      dtmf: "",
      startDate: "",
      endDate: "",
      search: "",
    });
    setPage(1);
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const list = listRes.data || [];
  const campaigns = (stats.byCampaign || [])
    .map((c) => c.campaign)
    .filter(Boolean);

  const pieData = [
    { name: "Answered", value: stats.answeredCalls || 0, color: T.green },
    { name: "Not Answered", value: stats.notAnsweredCalls || 0, color: T.red },
  ];

  const statCards = [
    {
      label: "Total Calls",
      value: stats.totalCalls,
      icon: <PhoneIcon sx={{ fontSize: 20 }} />,
      color: T.blue,
      bg: T.blueBg,
      border: T.blueBorder,
    },
    {
      label: "Answered",
      value: stats.answeredCalls,
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />,
      color: T.green,
      bg: T.greenBg,
      border: T.greenBorder,
    },
    {
      label: "Not Answered",
      value: stats.notAnsweredCalls,
      icon: <HighlightOffIcon sx={{ fontSize: 20 }} />,
      color: T.red,
      bg: T.redBg,
      border: T.redBorder,
    },
    {
      label: "Answer Rate",
      value: stats.answerRate != null ? `${stats.answerRate}%` : "—",
      icon: <TrendingUpIcon sx={{ fontSize: 20 }} />,
      color: T.purple,
      bg: T.purpleBg,
      border: T.purpleBorder,
    },
    {
      label: "Avg Duration",
      value: fmtDuration(stats.avgDuration),
      icon: <TimerOutlinedIcon sx={{ fontSize: 20 }} />,
      color: T.amber,
      bg: T.amberBg,
      border: T.amberBorder,
    },
  ];

  // Shared input sx
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      background: "#fff",
      "& fieldset": { borderColor: T.border },
      "&:hover fieldset": { borderColor: "#9ca3af" },
      "&.Mui-focused fieldset": { borderColor: T.blue },
    },
    "& .MuiInputLabel-root": { color: T.muted },
    "& .MuiInputLabel-root.Mui-focused": { color: T.blue },
  };
  const handleExport = async () => {
    setExporting(true);
    try {
      const qs = buildQS();
      const res = await fetch(`/voice/get-voice-report-export?${qs}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }, // adjust to your auth method
      });
      const json = await res.json();
      const rows = json.data || [];

      const fmtDur = (s) => {
        const n = parseInt(s, 10);
        if (!n || isNaN(n)) return "";
        return n < 60 ? `${n}s` : `${Math.floor(n / 60)}m ${n % 60}s`;
      };

      const sheetData = rows.map((r, i) => ({
        "#": i + 1,
        Mobile: r.mobile,
        Campaign: r.campaign_name,
        "CLI (Caller ID)": r.cli,
        "Answer Status": r.answer_status,
        "DTMF Key": r.dtmf || "",
        "Call Duration (billing)": fmtDur(r.billing_duration),
        "Patch Duration (s)": r.patch_duration || "",
        "Start Time": r.start_time,
        "Answer Time": r.answer_time,
        "End Time": r.end_time,
        "Hangup Cause": r.hangup_cause,
        "Agent Number": r.agent_number,
        "Agent Answer Time": r.agent_answer_time,
        "Bridge Hangup Cause": r.bridge_hangup_cause,
        "Retry Count": r.retry_count,
        "Recording URL": r.recording_file,
        "Created On": r.createdOn
          ? new Date(r.createdOn).toLocaleString("en-IN")
          : "",
      }));

      const ws = XLSX.utils.json_to_sheet(sheetData);

      // Column widths
      ws["!cols"] = [
        { wch: 4 },
        { wch: 14 },
        { wch: 22 },
        { wch: 16 },
        { wch: 14 },
        { wch: 10 },
        { wch: 20 },
        { wch: 18 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 22 },
        { wch: 16 },
        { wch: 20 },
        { wch: 20 },
        { wch: 12 },
        { wch: 50 },
        { wch: 22 },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Voice Report");

      // Summary sheet
      const summaryData = [
        ["Voice Campaign Report"],
        ["Generated On", new Date().toLocaleString("en-IN")],
        ["Total Records", rows.length],
        [],
        ["Filter Applied", "Value"],
        ["Answer Status", filters.answer_status || "All"],
        ["Campaign", filters.campaign_name || "All"],
        ["DTMF Key", filters.dtmf || "All"],
        ["Mobile Search", filters.search || "—"],
        ["From Date", filters.startDate || "—"],
        ["To Date", filters.endDate || "—"],
        [],
        ["Answer Status", "Count"],
        ...Object.entries(
          rows.reduce((acc, r) => {
            acc[r.answer_status] = (acc[r.answer_status] || 0) + 1;
            return acc;
          }, {})
        ).map(([k, v]) => [k, v]),
      ];
      const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
      wsSummary["!cols"] = [{ wch: 22 }, { wch: 28 }];
      XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

      const dateTag = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `voice-report-${dateTag}.xlsx`);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 3,
        p: { xs: 1.5, md: 3 },
        background: T.bg,
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, color: T.text, letterSpacing: "-0.5px" }}
          >
            Voice Campaign Reports
          </Typography>
          <Typography variant="body2" sx={{ color: T.muted, mt: 0.4 }}>
            {sLoad
              ? "Loading…"
              : `${stats.totalCalls ?? 0} total calls · ${
                  campaigns.length
                } campaigns`}
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="small"
          startIcon={exporting ? null : <DownloadIcon sx={{ fontSize: 17 }} />}
          onClick={handleExport}
          disabled={exporting}
          sx={{
            background: T.green,
            color: "#fff",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13,
            px: 2,
            boxShadow: "none",
            "&:hover": { background: "#15803d", boxShadow: "none" },
            "&.Mui-disabled": { background: T.borderLight, color: T.subtle },
          }}
        >
          {exporting
            ? "Exporting…"
            : `Export Excel${
                listRes.totalItems ? ` (${listRes.totalItems})` : ""
              }`}
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ ...card, mb: 3 }}>
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderBottom: `1px solid ${T.borderLight}`,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <FilterListIcon sx={{ fontSize: 16, color: T.muted }} />
          <Typography
            sx={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}
          >
            Filters
          </Typography>
          {activeFilterCount > 0 && (
            <Chip
              label={`${activeFilterCount} active`}
              size="small"
              sx={{
                height: 18,
                fontSize: 11,
                background: T.blueBg,
                color: T.blue,
                border: `1px solid ${T.blueBorder}`,
                fontWeight: 600,
              }}
            />
          )}
        </Box>
        <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search mobile number…"
                value={filters.search}
                onChange={(e) => setF("search", e.target.value)}
                sx={inputSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: T.subtle }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {[
              {
                label: "Answer Status",
                key: "answer_status",
                opts: ["Answered", "Not Answered", "Busy", "Failed"],
              },
              { label: "Campaign", key: "campaign_name", opts: campaigns },
              {
                label: "DTMF Key",
                key: "dtmf",
                opts: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
              },
            ].map(({ label, key, opts }) => (
              <Grid item xs={6} sm={4} md={2} key={key}>
                <FormControl fullWidth size="small" sx={inputSx}>
                  <InputLabel>{label}</InputLabel>
                  <Select
                    value={filters[key]}
                    label={label}
                    onChange={(e) => setF(key, e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {opts.map((o) => (
                      <MenuItem key={o} value={o}>
                        {o}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ))}

            <Grid item xs={6} sm={4} md={1.5}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From"
                value={filters.startDate}
                onChange={(e) => setF("startDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={1.5}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To"
                value={filters.endDate}
                onChange={(e) => setF("endDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={inputSx}
              />
            </Grid>

            {activeFilterCount > 0 && (
              <Grid item xs={12} md="auto">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={clearFilters}
                  sx={{
                    color: T.muted,
                    borderColor: T.border,
                    textTransform: "none",
                    fontSize: 13,
                    "&:hover": {
                      borderColor: "#9ca3af",
                      background: T.headerBg,
                    },
                  }}
                >
                  Clear all
                </Button>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Stat Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((s) => (
          <Grid item xs={6} sm={4} md={12 / 5} key={s.label}>
            <StatCard {...s} loading={sLoad} />
          </Grid>
        ))}
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Answer rate donut */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ ...card, height: 300 }}>
            <CardContent
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: "16px 18px",
                "&:last-child": { pb: "16px" },
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  color: T.muted,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.7,
                  mb: 0.5,
                }}
              >
                Answer Rate
              </Typography>
              <Box sx={{ flex: 1, position: "relative" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="46%"
                      innerRadius={58}
                      outerRadius={82}
                      paddingAngle={3}
                      dataKey="value"
                      strokeWidth={0}
                    >
                      {pieData.map((e) => (
                        <Cell key={e.name} fill={e.color} />
                      ))}
                    </Pie>
                    <RTooltip {...tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    position: "absolute",
                    top: "40%",
                    left: "50%",
                    transform: "translate(-50%,-50%)",
                    textAlign: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 24,
                      fontWeight: 800,
                      color: T.text,
                      lineHeight: 1,
                    }}
                  >
                    {stats.answerRate ?? 0}%
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: T.muted,
                      mt: 0.3,
                      fontWeight: 500,
                    }}
                  >
                    answer rate
                  </Typography>
                </Box>
              </Box>
              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                sx={{ mt: 0.5 }}
              >
                {pieData.map((e) => (
                  <Box
                    key={e.name}
                    sx={{ display: "flex", alignItems: "center", gap: 0.6 }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: e.color,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{ fontSize: 11, color: T.muted, fontWeight: 500 }}
                    >
                      {e.name}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Daily volume line chart */}
        <Grid item xs={12} md={5}>
          <Card sx={{ ...card, height: 300 }}>
            <CardContent
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: "16px 18px",
                "&:last-child": { pb: "16px" },
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  color: T.muted,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.7,
                  mb: 1,
                }}
              >
                Daily Volume
              </Typography>
              <Box sx={{ flex: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={stats.byDate || []}
                    margin={{ top: 4, right: 16, left: -24, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={T.borderLight}
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: T.subtle, fontSize: 10 }}
                      tickFormatter={(d) => d?.slice(5)}
                    />
                    <YAxis tick={{ fill: T.subtle, fontSize: 10 }} />
                    <RTooltip {...tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: 11, color: T.muted }} />
                    <Line
                      type="monotone"
                      dataKey="total"
                      stroke={T.blue}
                      strokeWidth={2.5}
                      dot={false}
                      name="Total"
                    />
                    <Line
                      type="monotone"
                      dataKey="answered"
                      stroke={T.green}
                      strokeWidth={2.5}
                      dot={false}
                      name="Answered"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* DTMF bar chart */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ ...card, height: 300 }}>
            <CardContent
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: "16px 18px",
                "&:last-child": { pb: "16px" },
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  color: T.muted,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.7,
                  mb: 1,
                }}
              >
                DTMF Keys Pressed
              </Typography>
              <Box sx={{ flex: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stats.byDtmf || []}
                    margin={{ top: 4, right: 8, left: -24, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={T.borderLight}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="dtmf"
                      tick={{
                        fill: T.textSecondary,
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    />
                    <YAxis tick={{ fill: T.subtle, fontSize: 10 }} />
                    <RTooltip {...tooltipStyle} />
                    <Bar
                      dataKey="count"
                      fill={T.purple}
                      radius={[4, 4, 0, 0]}
                      name="Calls"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Campaign performance horizontal bar */}
        <Grid item xs={12}>
          <Card sx={card}>
            <CardContent
              sx={{ p: "16px 18px", "&:last-child": { pb: "16px" } }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  color: T.muted,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.7,
                  mb: 1.5,
                }}
              >
                Campaign Performance
              </Typography>
              <ResponsiveContainer
                width="100%"
                height={Math.max(160, (stats.byCampaign?.length || 1) * 44)}
              >
                <BarChart
                  data={stats.byCampaign || []}
                  layout="vertical"
                  margin={{ top: 0, right: 20, left: 8, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={T.borderLight}
                    horizontal={false}
                  />
                  <XAxis
                    type="number"
                    tick={{ fill: T.subtle, fontSize: 10 }}
                  />
                  <YAxis
                    type="category"
                    dataKey="campaign"
                    tick={{ fill: T.textSecondary, fontSize: 12 }}
                    width={170}
                  />
                  <RTooltip {...tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 11, color: T.muted }} />
                  <Bar
                    dataKey="total"
                    fill="#93c5fd"
                    radius={[0, 4, 4, 0]}
                    name="Total"
                    barSize={10}
                  />
                  <Bar
                    dataKey="answered"
                    fill={T.green}
                    radius={[0, 4, 4, 0]}
                    name="Answered"
                    barSize={10}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Table */}
      <Card sx={card}>
        <Box
          sx={{
            px: 2.5,
            py: 1.8,
            borderBottom: `1px solid ${T.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontWeight: 700, color: T.text, fontSize: 15 }}>
            Call Logs
            {listRes.totalItems > 0 && (
              <Typography
                component="span"
                sx={{ fontSize: 12, color: T.muted, ml: 1, fontWeight: 400 }}
              >
                {listRes.totalItems} records
              </Typography>
            )}
          </Typography>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: T.headerBg }}>
                {[
                  "Mobile",
                  "Campaign",
                  "Status",
                  "DTMF",
                  "Duration",
                  "Agent",
                  "Hangup Cause",
                  "Date & Time",
                  "",
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      color: T.muted,
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.6,
                      borderBottom: `1px solid ${T.border}`,
                      whiteSpace: "nowrap",
                      py: 1.2,
                      px: 1.5,
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {lLoad ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    align="center"
                    sx={{ py: 6, color: T.muted, borderBottom: "none" }}
                  >
                    Loading…
                  </TableCell>
                </TableRow>
              ) : list.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    align="center"
                    sx={{ py: 6, color: T.muted, borderBottom: "none" }}
                  >
                    No records match your filters
                  </TableCell>
                </TableRow>
              ) : (
                list.map((row) => (
                  <TableRow
                    key={row._id}
                    sx={{
                      "&:hover": { background: "#fafafa" },
                      "& td": {
                        borderBottom: `1px solid ${T.borderLight}`,
                        fontSize: 13,
                        py: "8px",
                        px: 1.5,
                        color: T.textSecondary,
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontFamily: "monospace !important",
                        fontWeight: "600 !important",
                        color: `${T.text} !important`,
                      }}
                    >
                      {row.mobile}
                    </TableCell>

                    <TableCell>
                      <Tooltip title={row.campaign_name} placement="top">
                        <Typography
                          noWrap
                          sx={{
                            maxWidth: 160,
                            fontSize: 13,
                            color: T.textSecondary,
                          }}
                        >
                          {row.campaign_name}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={row.answer_status}
                        size="small"
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          height: 22,
                          background:
                            row.answer_status === "Answered"
                              ? T.greenBg
                              : T.redBg,
                          color:
                            row.answer_status === "Answered" ? T.green : T.red,
                          border: `1px solid ${
                            row.answer_status === "Answered"
                              ? T.greenBorder
                              : T.redBorder
                          }`,
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      {row.dtmf ? (
                        <Box
                          sx={{
                            display: "inline-flex",
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            background: T.purpleBg,
                            border: `1px solid ${T.purpleBorder}`,
                            alignItems: "center",
                            justifyContent: "center",
                            color: T.purple,
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          {row.dtmf}
                        </Box>
                      ) : (
                        <span style={{ color: T.subtle }}>—</span>
                      )}
                    </TableCell>

                    <TableCell
                      sx={{
                        fontFamily: "monospace !important",
                        fontWeight: "600 !important",
                        color: `${T.amber} !important`,
                      }}
                    >
                      {fmtDuration(row.billing_duration)}
                    </TableCell>

                    <TableCell
                      sx={{
                        fontFamily: "monospace !important",
                        fontSize: "12px !important",
                      }}
                    >
                      {row.agent_number || (
                        <span style={{ color: T.subtle }}>—</span>
                      )}
                    </TableCell>

                    <TableCell>
                      <Tooltip title={row.hangup_cause} placement="top">
                        <Typography
                          noWrap
                          sx={{ maxWidth: 150, fontSize: 12, color: T.muted }}
                        >
                          {row.hangup_cause || "—"}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell
                      sx={{
                        fontSize: "12px !important",
                        color: `${T.muted} !important`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmtDate(row.createdOn)}
                    </TableCell>

                    <TableCell>
                      {row.recording_file ? (
                        <Tooltip title="Play recording">
                          <IconButton
                            size="small"
                            onClick={() =>
                              window.open(row.recording_file, "_blank")
                            }
                            sx={{
                              color: T.blue,
                              p: 0.5,
                              "&:hover": { background: T.blueBg },
                            }}
                          >
                            <PlayArrowIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <span style={{ color: T.subtle }}>—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2.5,
            py: 1.5,
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <Typography sx={{ fontSize: 12, color: T.muted }}>
            Page {page} of {listRes.totalPages || 1}
          </Typography>
          <Pagination
            count={listRes.totalPages || 1}
            page={page}
            size="small"
            onChange={(_, v) => setPage(v)}
            sx={{
              "& .MuiPaginationItem-root": {
                color: T.muted,
                border: `1px solid ${T.border}`,
                "&.Mui-selected": {
                  background: T.blue,
                  color: "#fff",
                  border: "none",
                  fontWeight: 700,
                },
                "&:hover": { background: T.blueBg },
              },
            }}
          />
        </Box>
      </Card>
    </Box>
  );
};

export default VoiceFullReports;
